import { Component, OnInit, computed, effect, Signal, inject } from '@angular/core';
import { DataMartConstants } from '@r1-compass-library/constants';
import { DataMartCmt, DataMartPicklistValue, Play } from '@r1-compass-library/models';
import { DivisionCriteriaSourceSettings } from '@r1-compass-library/models/division/division-criteria-source-settings.model';
import { DataMartService } from '@r1-compass-library/services';
import { AssetStateMessageHandlingService } from '@r1-compass-library/services/asset-state-message-handling.service';
import { catchError, map, Observable, of, retry } from 'rxjs';
import { DefinitionForm, PlayDefinitionFormService } from './play-definition-form.service';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatSelectModule } from '@angular/material/select';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import {
    FormRowComponent,
    InstructionsComponent,
    InstructionsWithFieldsContainerComponent,
    TypeaheadSearchComponent,
} from '@r1-compass-library/components';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatRadioModule } from '@angular/material/radio';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { PlayStore } from '../store/play.store';
import { filterNil } from 'ngxtension/filter-nil';
import { injectParams } from 'ngxtension/inject-params';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { LifecycleRulesetListStore } from '../../lifecycle-rules/store/lifecycle-ruleset-list.store';
import { LifecycleRuleset } from '@r1-compass-library/models/lifecycle-rules/lifecycle-ruleset.model';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatDatepickerInputEvent, MatDatepickerModule } from '@angular/material/datepicker';
import { FaIconLibrary, FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faCalendar, faCircleQuestion } from '@fortawesome/pro-solid-svg-icons';
import { MatNativeDateModule } from '@angular/material/core';
import { GlobalLoggedInUserStore } from '@r1-compass-library/store/logged-in-user/global-logged-in-user.store';
import { FormUnsavedChangesWarningEntity } from '@r1-compass-library/guards/hasUnsavedChanges.guard';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SaveGuardStore } from '@r1-compass-library/guards/save-guard.store';
import { PlaySectionName } from '@r1-compass-library/features/validation-sync/section-names.model';
import { SyncValidationStateSection } from '@r1-compass-library/features/validation-sync/sync-validation-state.service';
import { ValidationStateService } from '@r1-compass-library/features/validation-sync/validation-state.service';
import { SyncValidationStateService, ValidationService } from '@r1-compass-library/features/validation-sync';
import { PlayFormMap, PlayValidationContext, PlayValidationService } from '../play-validation.service';
import { PlayValidationContextService } from '../play-validation-context.service';
import { PlayValidationStateService } from '../play-validation-state.service';
import { validLifecycleRuleValidator } from '../play-validators';

@Component({
    selector: 'r1-compass-app-play-definition',
    templateUrl: './play-definition.component.html',
    styleUrl: './play-definition.component.scss',
    imports: [
        FormRowComponent,
        InstructionsComponent,
        InstructionsWithFieldsContainerComponent,
        TypeaheadSearchComponent,
        FontAwesomeModule,
        MatButtonModule,
        MatButtonToggleModule,
        MatCheckboxModule,
        MatChipsModule,
        MatDatepickerModule,
        MatIconModule,
        MatInputModule,
        MatNativeDateModule,
        MatRadioModule,
        MatSelectModule,
        ReactiveFormsModule,
        RouterModule,
        MatTooltipModule,
    ],
    providers: [PlayDefinitionFormService],
})
export class PlayDefinitionComponent
    implements
        OnInit,
        FormUnsavedChangesWarningEntity,
        SyncValidationStateSection<PlaySectionName, Play, PlayValidationContext>
{
    protected readonly formStateService = inject(PlayDefinitionFormService);
    readonly #playStore = inject(PlayStore);
    readonly #route = inject(ActivatedRoute);
    readonly #lifecycleRulesetListStore = inject(LifecycleRulesetListStore);
    readonly #router = inject(Router);
    readonly #dataMartService = inject(DataMartService);
    readonly #globalLoggedInUserStore = inject(GlobalLoggedInUserStore);
    readonly #assetStateMessageHandlingService = inject(AssetStateMessageHandlingService);
    readonly #formSaveWarningStore = inject(SaveGuardStore);
    readonly #syncValidationStateService = inject(SyncValidationStateService);
    readonly #validationService = inject(PlayValidationService);
    readonly #validationContextService = inject(PlayValidationContextService);
    readonly #validationStateService = inject(PlayValidationStateService);

    faCalendar = faCalendar;
    definitionForm = this.formStateService.definitionForm;
    typeaheadValueControl = new FormControl<string>('');
    typeaheadDisplayControl = new FormControl<string>('');
    cmtTypeaheadResults: DataMartCmt[] = [];

    hierarchyLevel = '';
    typeaheadResults: string[] = [];
    chips: string[] = [];

    canDeactivate$ = this.#formSaveWarningStore.deriveCanDeactivate$(this.definitionForm);
    saveGuardHandlerFn = this.#formSaveWarningStore.formUnsavedChangesWarningEntityDefinition;
    readonly allowPartialSave = true;

    formValue = toSignal(this.definitionForm.valueChanges);
    blockSave = computed<boolean>(() => !this.formValue()?.playInternalName); // name is required by database table

    private readonly canDeactivate = toSignal(this.canDeactivate$, { requireSync: true });

    public disableSave = computed<boolean>(() =>
        this.#validationStateService.shouldDisableSave(
            this.#playStore.play(),
            this.canDeactivate(),
            this.#validationStateService.invalidSections(),
            'definition',
            this.blockSave()
        )
    );

    constructor() {
        const library = inject(FaIconLibrary);
        library.addIcons(faCircleQuestion);

        this.saveGuardHandlerFn({
            form: this.definitionForm,
            canDeactivate$: this.canDeactivate$,
            params: injectParams('id'),
            entity$: this.#playStore.play$,
            savedSoCanRoute$: this.#formSaveWarningStore.savedSoCanRoute$,
            route: this.#route,
            disableSave: this.disableSave,
        });

        this.subscribeToValidationState(
            this.definitionForm,
            'definition',
            this.#validationService,
            this.#validationStateService
        );

        effect(
            () => {
                this.definitionForm.controls.recommendationLifecycleRulesetId.addValidators(
                    validLifecycleRuleValidator(() => this.lifecycleRulesets())
                );
                this.definitionForm.controls.recommendationLifecycleRulesetId.updateValueAndValidity();
            },
            { debugName: 'update lifecycle ruleset validator on lifecycle ruleset list change' }
        );
    }

    playId = injectParams('id');
    private selectedLifecycleRuleId = toSignal(
        this.definitionForm.controls.recommendationLifecycleRulesetId.valueChanges,
        {
            initialValue: this.definitionForm.controls.recommendationLifecycleRulesetId.value,
        }
    );
    protected lifecycleRulesets = computed<LifecycleRuleset[]>(() => {
        return [...this.#lifecycleRulesetListStore.vm().assets]
            .sort(function (a, b) {
                return a.name!.localeCompare(b.name!);
            })
            .filter(rule => rule.enabled && rule.valid);
    });
    protected selectedInvalidLifecycleRuleset = computed<LifecycleRuleset | undefined>(() => {
        const selectedId = this.selectedLifecycleRuleId();
        return this.#lifecycleRulesetListStore
            .vm()
            .assets.find(rule => rule.id === selectedId && (!rule.enabled || !rule.valid));
    });
    divisionCriteriaSourceSettings: Signal<DivisionCriteriaSourceSettings | undefined> = computed(
        () => this.#globalLoggedInUserStore.selectedDivisionConfig()?.config?.criteriaSourceSettings
    );
    targetLevels = toSignal(
        this.getSalesHistoryTargetLevels(
            this.divisionCriteriaSourceSettings()?.salesHistoryTable,
            this.divisionCriteriaSourceSettings()?.salesHistoryCmtColumn
        ),
        { initialValue: [] }
    );
    userDefinedFields = this.#globalLoggedInUserStore.userDefinedFields;
    shownComponent: Signal<'Builder' | 'Importer' | undefined> = computed(() => {
        const queryParams: 'Builder' | 'Importer' | undefined = this.#route.snapshot.queryParams['generatorType']; // for new, unsaved Plays
        const playType: 'Builder' | 'Importer' | undefined = this.#playStore.shownComponent(); // for existing plays
        return queryParams ?? playType;
    });

    maxRecordsPerAccountOptions = computed<number[]>(() => {
        const divisionConfig = this.#globalLoggedInUserStore.selectedDivisionConfig();
        const multipleRecData = divisionConfig?.config?.mapping?.multipleRecommendations;
        if (multipleRecData?.enabled && multipleRecData.maxRecommendations) {
            return [...Array(multipleRecData.maxRecommendations).keys()].map(i => i + 1);
        }
        return [];
    });

    ruleDescription: Signal<string | undefined> = computed(
        () =>
            this.lifecycleRulesets()?.find(
                (ruleset: LifecycleRuleset) =>
                    +ruleset.id === this.formStateService.formState()?.recommendationLifecycleRulesetId
            )?.description
    );

    ruleLink: Signal<string | undefined> = computed(() => {
        const lifecycleId = this.formStateService.formState()?.recommendationLifecycleRulesetId;
        return this.#router.serializeUrl(this.#router.createUrlTree(['/lifecycle-rules', lifecycleId, 'definition']));
    });
    playIdFormControl: FormControl<number | undefined> = new FormControl(
        { value: undefined, disabled: true },
        { nonNullable: true }
    );
    salesforcePlayIdFormControl: FormControl<string | undefined> = new FormControl(
        { value: undefined, disabled: true },
        { nonNullable: true }
    );

    ngOnInit(): void {
        const playId: string = this.#route.snapshot.params['id'];
        if (playId && playId !== 'new') {
            this.#playStore.play$.pipe(filterNil()).subscribe(play => {
                this.playIdFormControl.setValue(play.id);
                this.salesforcePlayIdFormControl.setValue(play.playRecordId);
            });
            if (this.#playStore.play()?.id !== +playId) {
                this.#playStore.getPlay({ id: +playId });
            }
        } else {
            this.playIdFormControl.reset();
            this.salesforcePlayIdFormControl.reset();
        }

        this.#playStore.message$.pipe(filterNil()).subscribe(message => {
            this.#assetStateMessageHandlingService.handleMessage(message);
            this.#playStore.resetMessage();
        });
    }

    onTargetSelect() {
        this.cmtTypeaheadResults = [];
        this.typeaheadDisplayControl.setValue('');
        this.typeaheadValueControl.setValue('');
    }

    private formControlBindingMap = new Map<'name' | 'desc', boolean>().set('name', false).set('desc', false);

    fillBoundFormControl(fieldType: 'name' | 'desc') {
        if (fieldType === 'name') {
            if (!this.definitionForm.controls.playSalesforceName.value) {
                this.formControlBindingMap.set('name', true);
            }
            if (this.formControlBindingMap.get('name')) {
                this.definitionForm.controls.playSalesforceName.setValue(
                    this.definitionForm.controls.playInternalName.value
                );
            }
        } else if (fieldType === 'desc') {
            if (!this.definitionForm.controls.playSalesforceDescription.value) {
                this.formControlBindingMap.set('desc', true);
            }
            if (this.formControlBindingMap.get('desc')) {
                this.definitionForm.controls.playSalesforceDescription.setValue(
                    this.definitionForm.controls.playInternalDescription.value
                );
            }
        }
    }

    unbindFormControls(fieldType: 'name' | 'desc') {
        this.formControlBindingMap.set(fieldType, false);
    }

    onTypeaheadSearch(search: string) {
        this.#dataMartService.cmtSearch(+this.hierarchyLevel, search).subscribe(response => {
            this.cmtTypeaheadResults = response.entries;
        });
    }

    onTypeaheadSelection(event: MatAutocompleteSelectedEvent) {
        const entry: DataMartCmt = event.option.value as DataMartCmt;
        const level = this.targetLevels().find(lvl => lvl.value === this.hierarchyLevel);
        const chips = this.definitionForm.controls.targeting.value;
        const newChip = `CMT ${level?.value}: ${entry.name}`;
        this.definitionForm.controls.targeting.setValue([...(chips ?? []), newChip]);
        this.definitionForm.controls.targeting.markAllAsDirty();
        this.definitionForm.controls.targeting.markAllAsTouched();
        this.definitionForm.markAllAsDirty();
        this.typeaheadDisplayControl.setValue('');
        this.cmtTypeaheadResults = [];
    }

    removeChip(index: number) {
        const chips = this.definitionForm.controls.targeting.value;
        chips?.splice(index, 1);
        this.definitionForm.controls.targeting.setValue(chips);
        this.definitionForm.controls.targeting.markAllAsDirty();
        this.definitionForm.controls.targeting.markAllAsTouched();
        this.definitionForm.markAllAsDirty();
    }

    onDateInput(event: MatDatepickerInputEvent<Date>, formControl: FormControl<string | boolean | undefined>) {
        const value = event.value;
        if (value) {
            formControl.setValue(value.toISOString());
        }
    }

    onSave() {
        return this.formStateService.submit();
    }

    subscribeToValidationState(
        form: FormGroup<DefinitionForm>,
        sectionName: PlaySectionName,
        validationService: ValidationService<PlaySectionName, Play, PlayFormMap, PlayValidationContext>,
        validationStateService: ValidationStateService<PlaySectionName>
    ) {
        return this.#syncValidationStateService.subToValidationState(
            form,
            sectionName,
            validationService,
            validationStateService,
            this.#validationContextService.context
        );
    }

    private getSalesHistoryTargetLevels(
        tableName: string = DataMartConstants.DefaultSalesHistoryTable,
        columnName: string = DataMartConstants.DefaultSalesHistoryCmtColumn
    ): Observable<DataMartPicklistValue[]> {
        if (!tableName) {
            tableName = DataMartConstants.DefaultSalesHistoryTable;
        }
        if (!columnName) {
            columnName = DataMartConstants.DefaultSalesHistoryCmtColumn;
        }
        return this.#dataMartService.getPicklist(tableName, columnName).pipe(
            map(response => response.entries),
            retry(0),
            catchError(() => {
                return of<DataMartPicklistValue[]>();
            })
        );
    }
}
